import { Injectable, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { AutoCleanupService  } from './auto-cleanup.service';
import { IdleDetectionService } from './idle-detection.service';
import { MemoryWatchdogService} from './memory-watchdog.service';
import { SoftReloadService    } from './soft-reload.service';
import { TtlCacheService      } from './ttl-cache.service';
import { TimerRegistryService } from './timer-registry.service';

/**
 * MemoryManagerService — Top-level orchestrator for memory health.
 *
 * Call initialize() from AppComponent.ngOnInit() to activate:
 *   - Memory watchdog (samples heap every 60s)
 *   - Idle detection (soft reload after 2h inactivity)
 *   - Auto cleanup scheduler (runs every 30min)
 *   - Cache flush on cleanup cycle
 *   - Timer registry flush on soft reload
 *
 * This is the single entry point — consuming code does not need to
 * wire the individual services together.
 */
@Injectable({ providedIn: 'root' })
export class MemoryManagerService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private initialized = false;

  constructor(
    private readonly autoCleanup:    AutoCleanupService,
    private readonly idleDetection:  IdleDetectionService,
    private readonly watchdog:       MemoryWatchdogService,
    private readonly softReload:     SoftReloadService,
    private readonly cache:          TtlCacheService,
    private readonly timerRegistry:  TimerRegistryService,
  ) {}

  /**
   * Initialize all memory management subsystems.
   * Safe to call multiple times — subsequent calls are no-ops.
   *
   * @param idleThresholdMs   Idle duration before soft reload (default: 2h)
   * @param cleanupIntervalMs Auto-cleanup cycle interval (default: 30min)
   * @param watchdogIntervalMs Heap sample interval (default: 60s)
   */
  initialize(
    idleThresholdMs    = 2  * 60 * 60 * 1000,
    cleanupIntervalMs  = 30 * 60 * 1000,
    watchdogIntervalMs = 60 * 1000,
  ): void {
    if (this.initialized) return;
    this.initialized = true;

    console.log('[MemoryManager] Initializing memory management subsystems...');

    // 1. Register cleanup tasks
    this.autoCleanup.register({
      name: 'ttl-cache-flush',
      priority: 'high',
      fn: () => this.cache.flush(),
    });

    this.autoCleanup.register({
      name: 'gc-hint',
      priority: 'low',
      fn: () => this.gcHint(),
    });

    // 2. Start auto cleanup scheduler
    this.autoCleanup.start(cleanupIntervalMs);

    // 3. Start idle detection — soft reload on inactivity
    this.idleDetection.start(idleThresholdMs);
    this.idleDetection.idle$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        console.log('[MemoryManager] Idle threshold reached — triggering soft reload');
        this.softReload.softReload();
      });

    // 4. Start memory watchdog — soft reload on critical heap
    this.watchdog.start(watchdogIntervalMs, () => {
      console.log('[MemoryManager] Critical heap threshold crossed — triggering soft reload');
      this.softReload.softReload();
    });

    // 5. Clear timers and cache before each soft reload
    this.softReload.beforeReload$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cache.flush();
        // Note: Do NOT call timerRegistry.clearAll() here for app-critical timers.
        // Only clear feature-specific timers that components register by name.
        console.log('[MemoryManager] Pre-reload cleanup complete');
      });

    console.log('[MemoryManager] ✅ All subsystems active');
    console.log(`[MemoryManager]   Idle threshold:    ${idleThresholdMs / 60_000}min`);
    console.log(`[MemoryManager]   Cleanup interval:  ${cleanupIntervalMs / 60_000}min`);
    console.log(`[MemoryManager]   Watchdog interval: ${watchdogIntervalMs / 1000}s`);
  }

  /** Manually trigger a cleanup + soft reload */
  async forceReset(): Promise<void> {
    console.log('[MemoryManager] Manual force reset initiated');
    await this.autoCleanup.runNow();
    await this.softReload.softReload();
  }

  /** Get a status summary string for logging/diagnostics */
  getStatus(): string {
    const mem = this.watchdog.getReport();
    const uptime = this.softReload.uptimeFormatted;
    const cacheSize = this.cache.size;
    const timers = this.timerRegistry.intervalCount;
    return `Uptime: ${uptime} | ${mem} | Cache: ${cacheSize} entries | Timers: ${timers}`;
  }

  private gcHint(): void {
    try {
      const dummy = new Array(50_000).fill(null);
      dummy.length = 0;
    } catch { /* ignore */ }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.autoCleanup.stop();
    this.idleDetection.stop();
    this.watchdog.stop();
  }
}
